import { TickIcon } from '@commenda/icons';

import { memo } from 'react';
import { Button } from '../Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../Dropdown/_base';

export interface FilterOption<T extends string> {
  value: T;
  label: string;
}

interface FilterDropdownProps<T extends string> {
  triggerIcon: React.ReactNode;
  triggerLabel: string;
  options: FilterOption<T>[];
  selectedValue?: T;
  onSelect: (value: T | undefined) => void;
  _className?: string;
}

export const FilterDropdown = memo(
  <T extends string>({
    triggerIcon,
    triggerLabel,
    options,
    selectedValue,
    onSelect,
    _className,
  }: FilterDropdownProps<T>) => {
    const handleSelect = (optionValue: T) => {
      onSelect(selectedValue === optionValue ? undefined : optionValue);
    };

    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="secondary" size="sm" className={_className}>
            {triggerIcon}
            <span>{triggerLabel}</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="z-10 w-56">
          {options.map((option) => (
            <DropdownMenuItem
              key={option.value}
              className="flex w-full cursor-pointer justify-between"
              onClick={() => handleSelect(option.value)}
            >
              <span>{option.label}</span>
              {selectedValue === option.value && (
                <TickIcon className="size-4" />
              )}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }
);

(
  FilterDropdown as React.MemoExoticComponent<
    <T extends string>(props: FilterDropdownProps<T>) => JSX.Element
  >
).displayName = 'FilterDropdown';
